/** The sound vocabulary, and the one place an event becomes a sound.
 *
 * This file is pure: no `AudioContext`, no `window`, nothing that only exists
 * in a browser. That is deliberate - what a game event *means* is worth a test,
 * and a test that has to stand up a Web Audio graph to ask "does capturing a
 * town make a noise" is a test nobody writes. The graph lives in `synth.ts` and
 * the wiring in `engine.ts`; this is the dictionary between them.
 *
 * The vocabulary is small on purpose. Twenty-odd cues cover every event the
 * kernel emits because most events are variations on four things a player is
 * being told: something of yours moved, something of yours grew, something hit
 * something, and something ended. A cue per event type would be sixty sounds,
 * fifty of which would be the same sound, and the mix would be mud.
 */

import type { GameEvent } from "@game/core";

export const CUES = [
  // ---- shell
  "ui.click",
  "ui.close",
  "ui.refused",
  "prompt.open",

  // ---- calendar
  "turn.start",
  "week.start",
  "month.start",

  // ---- adventure
  "hero.move",
  "hero.levelup",
  "hero.hire",
  "hero.defeated",
  "object.pickup",
  "object.capture",
  "resource.gain",
  "resource.spend",
  "artifact.gain",
  "spell.learn",

  // ---- town
  "town.build",
  "town.capture",
  "town.recruit",

  // ---- battle
  "battle.start",
  "battle.melee",
  "battle.ranged",
  "battle.death",
  "battle.spell",
  "battle.wall",
  "battle.morale",
  "battle.luck",

  // ---- the end
  "game.win",
  "game.lose",
] as const;

export type Cue = (typeof CUES)[number];

/** Which bus a cue is mixed on. Music is scheduled, not cued. */
export type Bus = "sfx" | "ui";

export const BUS_OF: Readonly<Record<Cue, Bus>> = Object.freeze({
  "ui.click": "ui", "ui.close": "ui", "ui.refused": "ui", "prompt.open": "ui",
  "turn.start": "ui", "week.start": "ui", "month.start": "ui",
  "hero.move": "sfx", "hero.levelup": "sfx", "hero.hire": "sfx",
  "hero.defeated": "sfx", "object.pickup": "sfx", "object.capture": "sfx",
  "resource.gain": "sfx", "resource.spend": "sfx", "artifact.gain": "sfx",
  "spell.learn": "sfx",
  "town.build": "sfx", "town.capture": "sfx", "town.recruit": "sfx",
  "battle.start": "sfx", "battle.melee": "sfx", "battle.ranged": "sfx",
  "battle.death": "sfx", "battle.spell": "sfx", "battle.wall": "sfx",
  "battle.morale": "sfx", "battle.luck": "sfx",
  "game.win": "sfx", "game.lose": "sfx",
});

/**
 * The cue an event asks for, or `null` for an event that should be silent.
 *
 * Silence is a decision, not an omission. `HeroMovementSpent` fires once per
 * hero per day and says nothing a player did; `BattleStackTurn` fires for every
 * stack of every round; `Checkpoint` is a diagnostic. A sound on any of them is
 * a sound the player learns to stop hearing, which costs the cues that matter.
 */
export function cueFor(e: GameEvent): Cue | null {
  switch (e.type) {
    case "TurnStarted": return "turn.start";
    case "WeekStarted": return "week.start";
    case "MonthStarted": return "month.start";
    case "GameOver": return null; // the shell knows who won; see `endCue`.

    case "HeroMoved": return "hero.move";
    case "HeroLeveledUp": return "hero.levelup";
    case "HeroHired": return "hero.hire";
    case "HeroDefeated": return "hero.defeated";

    case "ObjectVisited": return "object.pickup";
    case "ObjectCaptured": return "object.capture";
    case "ResourceGained": return "resource.gain";
    case "ResourceSpent": return "resource.spend";
    case "ArtifactGained": return "artifact.gain";
    case "SpellLearned": return "spell.learn";

    case "TownCaptured": return "town.capture";
    case "BuildingBuilt": return "town.build";
    case "CreaturesRecruited": return "town.recruit";

    case "InteractionOpened": return "prompt.open";

    case "BattleStarted": return "battle.start";
    case "BattleAttacked": return e.ranged ? "battle.ranged" : "battle.melee";
    case "BattleStackDied": return "battle.death";
    case "BattleSpellCast": return "battle.spell";
    case "BattleWallHit": return "battle.wall";
    case "BattleMorale": return "battle.morale";
    case "BattleLuck": return "battle.luck";

    default: return null;
  }
}

/** The one cue that depends on who is listening rather than on what happened. */
export const endCue = (won: boolean): Cue => (won ? "game.win" : "game.lose");

/**
 * A burst of events becomes a burst of sound, and a burst of sound is noise.
 *
 * The computer's turn arrives as several hundred events at once and a battle
 * round as a few dozen. Played straight, thirty `battle.melee`s inside one
 * frame sum to thirty times the amplitude and clip the master bus flat.
 *
 * Two rules, and they do different jobs. `MAX_PER_FLUSH` is the anti-clipping
 * rule: no more than three of any one cue come out of a single drain, whatever
 * the cue. `RATE_LIMIT_MS` is the legibility rule: a cue that would repeat
 * inside its own gap is *moved later* rather than dropped, so a round of melee
 * lands as three blows a sixteenth of a second apart - a flurry - instead of
 * one blow or one crunch.
 *
 * Moving rather than dropping is the part worth keeping. The first draft
 * dropped, and the effect was that a cue with a gap could never sound more than
 * once per drain: a whole battle round was a single click.
 */
export const RATE_LIMIT_MS: Readonly<Partial<Record<Cue, number>>> = Object.freeze({
  "hero.move": 90,
  "resource.gain": 110,
  "resource.spend": 110,
  "battle.melee": 65,
  "battle.ranged": 65,
  "battle.death": 90,
  "object.pickup": 110,
  "turn.start": 400,
});

export const MAX_PER_FLUSH = 3;

/** A cue and how long after the drain it should sound, in milliseconds. */
export interface CueHit {
  readonly cue: Cue;
  readonly delayMs: number;
}

/**
 * Thin a drained event list down to what is worth hearing, and say when.
 *
 * `now` is passed in rather than read, because this is the pure half: the test
 * drives the clock, and `engine.ts` passes the real one. `lastAt` holds, per
 * cue, the earliest time that cue may next sound - so it is written forward
 * past `now`, which is what spreads a flurry.
 */
export function selectCues(
  events: readonly GameEvent[],
  now: number,
  lastAt: Map<Cue, number>,
): readonly CueHit[] {
  const out: CueHit[] = [];
  const seen = new Map<Cue, number>();
  for (const e of events) {
    const cue = cueFor(e);
    if (cue === null) continue;

    const count = seen.get(cue) ?? 0;
    if (count >= MAX_PER_FLUSH) continue;
    seen.set(cue, count + 1);

    const gap = RATE_LIMIT_MS[cue];
    if (gap === undefined) { out.push({ cue, delayMs: 0 }); continue; }

    const earliest = lastAt.get(cue) ?? now;
    const when = earliest > now ? earliest : now;
    lastAt.set(cue, when + gap);
    out.push({ cue, delayMs: when - now });
  }
  return out;
}
